
import React from "react";
import { Table, TableBody, TableHeader as TableHeaderContainer } from "@/components/ui/table";
import TableHeaderRow from "./TableHeader";
import TableBodyComponent from "./TableBody";
import { useStrategiesTable } from "./StrategiesTableProvider";
import { StrategyWithDetails } from "@/types/strategy-types";
import { sortStrategies, ITEMS_PER_PAGE } from "./tableUtils";

interface StrategiesDataTableProps {
  strategies: StrategyWithDetails[];
  onToggleStatus: (strategyId: string, currentStatus: boolean) => Promise<void>;
  onToggleActive: (strategyId: string, currentActive: boolean) => Promise<void>;
  onApproveStrategy: (strategyId: string, approved: boolean) => Promise<void>;
  userRole?: string | null;
}

const StrategiesDataTable = ({
  strategies,
  onToggleStatus,
  onToggleActive,
  onApproveStrategy,
  userRole
}: StrategiesDataTableProps) => {
  const {
    searchTerm,
    statusFilter,
    approvalFilter,
    providerFilter,
    columnFilters,
    sortField,
    sortOrder,
    currentPage,
    setEditingStrategy,
    setDeletingStrategyId,
    setPreviewStrategy,
    handleColumnFilterChange,
    handleSort
  } = useStrategiesTable();

  // Filter strategies based on current filters
  const filteredStrategies = strategies.filter((strategy) => {
    const matchesSearch = strategy.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "pro" && (strategy.plan === 'pro' || strategy.plan === 'premium')) || 
      (statusFilter === "free" && strategy.plan === 'free');
    
    const matchesApproval = approvalFilter === "all" ||
      (approvalFilter === "approved" && strategy.approved) ||
      (approvalFilter === "pending" && !strategy.approved);
    
    const matchesProvider = providerFilter === "all" || 
      strategy.strategy_provider_id === providerFilter;
    
    const matchesColumnFilters = 
      (!columnFilters.name || strategy.name?.toLowerCase().includes(columnFilters.name.toLowerCase())) &&
      (!columnFilters.exchange || strategy.exchange?.toLowerCase().includes(columnFilters.exchange.toLowerCase())) &&
      (!columnFilters.minInvestment || strategy.min_investment?.toString().includes(columnFilters.minInvestment)) &&
      (!columnFilters.riskLevel || strategy.risk_level?.toString() === columnFilters.riskLevel);
    
    return matchesSearch && matchesStatus && matchesApproval && matchesProvider && matchesColumnFilters;
  });

  // Sort and paginate
  const sortedStrategies = sortStrategies(filteredStrategies, sortField, sortOrder);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedStrategies = sortedStrategies.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeaderContainer>
          <TableHeaderRow
            onSort={handleSort}
            columnFilters={columnFilters}
            onFilterChange={handleColumnFilterChange}
          />
        </TableHeaderContainer>
        <TableBody>
          <TableBodyComponent
            strategies={paginatedStrategies}
            onToggleStatus={onToggleStatus}
            onToggleActive={onToggleActive}
            onApproveStrategy={onApproveStrategy}
            onEdit={setEditingStrategy}
            onDelete={setDeletingStrategyId}
            onPreview={setPreviewStrategy}
            userRole={userRole}
          />
        </TableBody>
      </Table>
    </div>
  );
};

export default StrategiesDataTable;
